import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, X, LayoutGrid, List } from 'lucide-react';

interface ClientFiltersProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  clientTypeFilter: string;
  setClientTypeFilter: (type: string) => void;
  priorityFilter: string;
  setPriorityFilter: (priority: string) => void;
  legalAreaFilter: string;
  setLegalAreaFilter: (area: string) => void;
  totalClients: number;
  filteredCount: number;
  typeCounts?: { all: number; person: number; company: number };
  viewMode?: 'card' | 'list';
  setViewMode?: (mode: 'card' | 'list') => void;
}

const legalAreas = [
  'Direito Civil', 'Direito Criminal', 'Direito Trabalhista',
  'Direito Empresarial', 'Direito Tributário', 'Direito de Família',
  'Direito Imobiliário', 'Direito do Consumidor', 'Direito Previdenciário',
  'Direito Digital', 'Direito Ambiental', 'Direito Administrativo'
];

const priorityLabels: Record<string, string> = { vip: 'VIP', high: 'Alta', medium: 'Média', low: 'Baixa' };

const triggerClass =
  'h-10 w-full rounded-xl border-slate-200 bg-white text-sm text-slate-700 shadow-none hover:border-slate-300 focus:ring-[#c4ab83]/30 lg:w-[11rem]';

export const ClientFilters = ({
  searchTerm,
  setSearchTerm,
  clientTypeFilter,
  setClientTypeFilter,
  priorityFilter,
  setPriorityFilter,
  legalAreaFilter,
  setLegalAreaFilter,
  totalClients,
  filteredCount,
  typeCounts,
  viewMode,
  setViewMode,
}: ClientFiltersProps) => {
  const activeFilters = [
    searchTerm && { key: 'search', label: `“${searchTerm}”`, clear: () => setSearchTerm('') },
    clientTypeFilter !== 'all' && {
      key: 'type',
      label: clientTypeFilter === 'person' ? 'Pessoa física' : 'Empresa',
      clear: () => setClientTypeFilter('all'),
    },
    priorityFilter !== 'all' && { key: 'priority', label: `Prioridade ${priorityLabels[priorityFilter] || priorityFilter}`, clear: () => setPriorityFilter('all') },
    legalAreaFilter !== 'all' && { key: 'area', label: legalAreaFilter, clear: () => setLegalAreaFilter('all') },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  const clearAll = () => {
    setSearchTerm('');
    setClientTypeFilter('all');
    setPriorityFilter('all');
    setLegalAreaFilter('all');
  };

  return (
    <div className="space-y-3">
      <div className="sticky top-2 z-20 rounded-2xl border border-slate-200/80 bg-white/85 p-2 shadow-sm backdrop-blur-md">
        <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar por nome, e-mail ou CPF/CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-10 rounded-xl border-slate-200 bg-white pl-10 text-sm shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30"
            />
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex">
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className={triggerClass}>
                <SelectValue placeholder="Prioridade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as prioridades</SelectItem>
                <SelectItem value="vip">VIP</SelectItem>
                <SelectItem value="high">Alta</SelectItem>
                <SelectItem value="medium">Média</SelectItem>
                <SelectItem value="low">Baixa</SelectItem>
              </SelectContent>
            </Select>

            <Select value={legalAreaFilter} onValueChange={setLegalAreaFilter}>
              <SelectTrigger className={triggerClass}>
                <SelectValue placeholder="Área jurídica" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as áreas</SelectItem>
                {legalAreas.map((area) => (
                  <SelectItem key={area} value={area}>
                    {area}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex shrink-0 items-center justify-between gap-2">
            <div className="inline-flex rounded-xl bg-slate-100 p-1">
              {[
                { value: 'all', label: 'Todos', count: typeCounts?.all },
                { value: 'person', label: 'Pessoa física', count: typeCounts?.person },
                { value: 'company', label: 'Empresa', count: typeCounts?.company },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setClientTypeFilter(option.value)}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-medium transition-all duration-200 ${
                    clientTypeFilter === option.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {option.label}
                  {option.count !== undefined && (
                    <span className={`tabular-nums ${clientTypeFilter === option.value ? 'text-[#9a7f52]' : 'text-slate-400'}`}>
                      {option.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {viewMode && setViewMode && (
              <div className="inline-flex rounded-xl bg-slate-100 p-1">
                {([
                  { value: 'card', icon: LayoutGrid, label: 'Cartões' },
                  { value: 'list', icon: List, label: 'Lista' },
                ] as const).map(({ value, icon: Icon, label }) => (
                  <button
                    key={value}
                    type="button"
                    title={label}
                    onClick={() => setViewMode(value)}
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-200 ${
                      viewMode === value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <div className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500">
          <span>
            <strong className="font-semibold text-slate-900">{filteredCount}</strong>
            {filteredCount !== totalClients && <> de {totalClients}</>} cliente{filteredCount === 1 ? '' : 's'}
          </span>
          {activeFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              onClick={filter.clear}
              className="inline-flex max-w-[16rem] items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
              title="Remover filtro"
            >
              <span className="truncate">{filter.label}</span>
              <X className="h-3 w-3 shrink-0" />
            </button>
          ))}
          {activeFilters.length > 1 && (
            <button type="button" onClick={clearAll} className="text-xs font-medium text-[#9a7f52] hover:underline">
              Limpar tudo
            </button>
          )}
        </div>
        <span className="text-xs text-slate-400">Ordem alfabética</span>
      </div>
    </div>
  );
};
